/**
 * @fileoverview Shared contact-role definitions
 *
 * Purpose: Single source of truth for `ContactSourceKind` plus its display
 * metadata (label, badge colour, icon, summary slug). Lifted out of the
 * job-CRM `phases/contacts/types.ts` so that:
 *
 *   - the precedent library & wizard can encode `cr:*` tags against the
 *     same enum without depending on the contacts UI module, and
 *   - the contacts UI keeps importing from `phases/contacts/types`
 *     unchanged (those exports re-export from here).
 *
 * Mirrors the `contactSourceKindValidator` union defined in
 * `convex/contacts/jobContacts/schema.ts`.
 */

import {
  Briefcase,
  Building2,
  Contact,
  HandCoins,
  Handshake,
  Landmark,
  Receipt,
  Scale,
  ShieldCheck,
  UserCog,
  UserMinus,
  UserSquare,
  Users,
  Warehouse,
  type LucideIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ContactSourceKind =
  | "corporate"
  | "related_party"
  | "security_interest"
  | "employee"
  | "liability"
  | "loan"
  | "ato_sro"
  | "asset_valuation";

/**
 * Display metadata per source kind: short label, badge colour, icon, and
 * the slug of the summaries sub-route to deep-link to from the detail panel.
 */
export interface SourceKindMeta {
  label: string;
  /** Short label used in the filter chip strip. */
  shortLabel: string;
  badgeColor:
    | "gray"
    | "blue"
    | "red"
    | "green"
    | "emerald"
    | "amber"
    | "teal"
    | "indigo"
    | "purple"
    | "pink"
    | "orange"
    | "violet"
    | "sky";
  icon: LucideIcon;
  /**
   * Summaries-route slug. Used to deep-link from the detail panel back into
   * the agent-specific entry page.
   */
  summarySlug: string;
}

export const SOURCE_KIND_META: Record<ContactSourceKind, SourceKindMeta> = {
  corporate: {
    label: "Corporate Structure",
    shortLabel: "Corporate",
    badgeColor: "indigo",
    icon: Building2,
    summarySlug: "corporate",
  },
  related_party: {
    label: "Related Parties",
    shortLabel: "Related Parties",
    badgeColor: "purple",
    icon: Users,
    summarySlug: "related-parties",
  },
  security_interest: {
    label: "Security Interests",
    shortLabel: "Secured Parties",
    badgeColor: "amber",
    icon: ShieldCheck,
    summarySlug: "security-interests",
  },
  employee: {
    label: "Employees",
    shortLabel: "Employees",
    badgeColor: "teal",
    icon: UserSquare,
    summarySlug: "employment",
  },
  liability: {
    label: "Creditors",
    shortLabel: "Creditors",
    badgeColor: "red",
    icon: Receipt,
    summarySlug: "creditors",
  },
  loan: {
    label: "Loans",
    shortLabel: "Loan Counterparties",
    badgeColor: "violet",
    icon: HandCoins,
    summarySlug: "loans",
  },
  ato_sro: {
    label: "Tax Authorities",
    shortLabel: "ATO/SRO",
    badgeColor: "blue",
    icon: Landmark,
    summarySlug: "ato-sro",
  },
  asset_valuation: {
    label: "Asset Valuers",
    shortLabel: "Valuers",
    badgeColor: "emerald",
    icon: Warehouse,
    summarySlug: "assets",
  },
};

/** Stable order in which source kinds appear in filter chips and badges. */
export const SOURCE_KIND_ORDER: ReadonlyArray<ContactSourceKind> = [
  "corporate",
  "related_party",
  "security_interest",
  "employee",
  "liability",
  "loan",
  "ato_sro",
  "asset_valuation",
];

// ---------------------------------------------------------------------------
// Canonical contact roles
//
// Fine-grained role taxonomy derived from source entity data during
// reconcile. Mirrors `contactRoleValidator` in
// `convex/contacts/jobContacts/schema.ts`.
// Used for UI display and document-generation grouping.
// ---------------------------------------------------------------------------

export type ContactRole =
  | "director"
  | "former_director"
  | "secretary"
  | "officer"
  | "shareholder"
  | "secured_creditor"
  | "unsecured_creditor"
  | "employee"
  | "tax_authority"
  | "related_entity"
  | "related_individual"
  | "professional"
  | "lender"
  | "borrower"
  | "petitioning_creditor"
  | "applicant_creditor"
  | "bankrupt"
  | "other";

export type BadgeColor = SourceKindMeta["badgeColor"];

export interface RoleMeta {
  label: string;
  pluralLabel: string;
  badgeColor: BadgeColor;
  icon: LucideIcon;
}

export const ROLE_META: Record<ContactRole, RoleMeta> = {
  director: {
    label: "Director",
    pluralLabel: "Directors",
    badgeColor: "indigo",
    icon: UserSquare,
  },
  former_director: {
    label: "Former Director",
    pluralLabel: "Former Directors",
    badgeColor: "gray",
    icon: UserMinus,
  },
  secretary: {
    label: "Secretary",
    pluralLabel: "Secretaries",
    badgeColor: "sky",
    icon: UserCog,
  },
  officer: {
    label: "Officer",
    pluralLabel: "Officers",
    badgeColor: "blue",
    icon: UserSquare,
  },
  shareholder: {
    label: "Shareholder",
    pluralLabel: "Shareholders",
    badgeColor: "purple",
    icon: Users,
  },
  secured_creditor: {
    label: "Secured Creditor",
    pluralLabel: "Secured Creditors",
    badgeColor: "amber",
    icon: ShieldCheck,
  },
  unsecured_creditor: {
    label: "Unsecured Creditor",
    pluralLabel: "Unsecured Creditors",
    badgeColor: "red",
    icon: Receipt,
  },
  employee: {
    label: "Employee",
    pluralLabel: "Employees",
    badgeColor: "teal",
    icon: UserSquare,
  },
  tax_authority: {
    label: "Tax Authority",
    pluralLabel: "Tax Authorities",
    badgeColor: "blue",
    icon: Landmark,
  },
  related_entity: {
    label: "Related Entity",
    pluralLabel: "Related Entities",
    badgeColor: "orange",
    icon: Building2,
  },
  related_individual: {
    label: "Related Individual",
    pluralLabel: "Related Individuals",
    badgeColor: "pink",
    icon: Users,
  },
  professional: {
    label: "Professional",
    pluralLabel: "Professionals",
    badgeColor: "emerald",
    icon: Briefcase,
  },
  lender: {
    label: "Lender",
    pluralLabel: "Lenders",
    badgeColor: "violet",
    icon: HandCoins,
  },
  borrower: {
    label: "Borrower",
    pluralLabel: "Borrowers",
    badgeColor: "green",
    icon: HandCoins,
  },
  petitioning_creditor: {
    label: "Petitioning Creditor",
    pluralLabel: "Petitioning Creditors",
    badgeColor: "amber",
    icon: Scale,
  },
  applicant_creditor: {
    label: "Applicant Creditor",
    pluralLabel: "Applicant Creditors",
    badgeColor: "amber",
    icon: Scale,
  },
  bankrupt: {
    label: "Bankrupt",
    pluralLabel: "Bankrupts",
    badgeColor: "red",
    icon: UserSquare,
  },
  other: {
    label: "Other",
    pluralLabel: "Other",
    badgeColor: "gray",
    icon: Contact,
  },
};

/** Stable order in which roles appear in badges, filters, and groupings. */
export const ROLE_ORDER: ReadonlyArray<ContactRole> = [
  "bankrupt",
  "director",
  "former_director",
  "secretary",
  "officer",
  "shareholder",
  "secured_creditor",
  "unsecured_creditor",
  "employee",
  "tax_authority",
  "related_entity",
  "related_individual",
  "professional",
  "lender",
  "borrower",
  "petitioning_creditor",
  "applicant_creditor",
  "other",
];

// ---------------------------------------------------------------------------
// Tag helpers used by the precedent library / generate-precedents wizard
// ---------------------------------------------------------------------------

/**
 * Prefix used for contact-role tags stored in `precedentTemplates.tags`.
 * Parallel to `JOB_STATUS_TAG_PREFIX` (`js:`) and `TAG_GROUP_PREFIX`
 * (`tg:`) defined in `src/lib/precedent-templates.ts`.
 */
export const CONTACT_ROLE_TAG_PREFIX = "cr:" as const;

/** Build the canonical tag string for a contact role. */
export function contactRoleTag(role: ContactRole): string {
  return `${CONTACT_ROLE_TAG_PREFIX}${role}`;
}

/** Type guard for whether a raw string is a known `ContactRole`. */
export function isContactRole(value: string): value is ContactRole {
  return ROLE_ORDER.some((role) => role === value);
}

/**
 * Parse a `cr:*` tag back into its `ContactRole`. Returns `null`
 * for tags that don't carry the prefix or whose value isn't a known role.
 */
export function parseContactRoleTag(tag: string): ContactRole | null {
  if (!tag.startsWith(CONTACT_ROLE_TAG_PREFIX)) return null;
  const value = tag.slice(CONTACT_ROLE_TAG_PREFIX.length);
  return isContactRole(value) ? value : null;
}

/**
 * All contact roles, in canonical display order. Aliased from
 * `ROLE_ORDER` so call-sites that only care about precedent tags
 * read more naturally.
 */
export const CONTACT_ROLE_KINDS: ReadonlyArray<ContactRole> = ROLE_ORDER;

/**
 * Prefix for user-defined job-contact role tags. The value is the
 * `jobContactRoles` row id, so tags survive role renames. Parallel to the
 * built-in `cr:<enum>` prefix and the firm-side `fcc:<id>` prefix.
 */
export const JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX = "jcr:" as const;

/** Build the tag string for a custom job-contact role id. */
export function jobContactCustomRoleTag(roleId: string): string {
  return `${JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX}${roleId}`;
}

/** Parse a `jcr:*` tag back into the custom role id, or `null`. */
export function parseJobContactCustomRoleTag(tag: string): string | null {
  if (!tag.startsWith(JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX)) return null;
  const value = tag.slice(JOB_CONTACT_CUSTOM_ROLE_TAG_PREFIX.length);
  return value || null;
}

/**
 * Maps each `cr:*` contact role to the agent-source kinds whose data
 * may be inserted as `contact_job_<source_kind>_*` fields when the
 * template is tagged with that role. Drives:
 *
 *  1. The Insert Field menu in the template editor — only sources
 *     listed here for the template's tagged roles appear as
 *     `Job Contact: <Source>: <Attribute>` groups.
 *  2. The wizard's per-(contact × source-link) fan-out — when a
 *     template body references any `contact_job_<source_kind>_*` key,
 *     each ticked contact's `sourceLinks` are filtered to the union
 *     of source kinds across the template's tagged roles.
 *
 * A role with no mapped source kinds (e.g. `professional`, `other`)
 * simply offers no source fields — the basic Job Contact group still
 * works as before.
 */
export const ROLE_TO_SOURCE_KINDS: Record<
  ContactRole,
  ReadonlyArray<ContactSourceKind>
> = {
  director: ["corporate", "related_party", "asset_valuation"],
  former_director: ["corporate", "related_party", "asset_valuation"],
  secretary: ["corporate"],
  officer: ["corporate"],
  shareholder: ["corporate", "related_party", "asset_valuation"],
  secured_creditor: ["security_interest"],
  unsecured_creditor: ["liability"],
  employee: ["employee"],
  tax_authority: ["ato_sro"],
  related_entity: ["related_party", "corporate"],
  related_individual: ["related_party"],
  professional: [],
  lender: ["loan"],
  borrower: ["loan"],
  petitioning_creditor: [],
  applicant_creditor: [],
  bankrupt: ["corporate"],
  other: [],
};

/**
 * Union of the source kinds surfaced for any role in the given list.
 * Helper used by both the editor's Insert Field menu and the wizard's
 * fan-out — pass the roles a template is tagged with, get back the
 * source kinds whose data the template author may pull through.
 */
export function sourceKindsForRoles(
  roles: ReadonlyArray<ContactRole>,
): ContactSourceKind[] {
  const seen = new Set<ContactSourceKind>();
  for (const role of roles) {
    for (const kind of ROLE_TO_SOURCE_KINDS[role] ?? []) {
      seen.add(kind);
    }
  }
  return Array.from(seen);
}

// ---------------------------------------------------------------------------
// Firm-contact categories
//
// Firm contacts (the org-wide directory) are classified by `category`
// rather than by agent-source `kind`. A precedent template can fan out
// over firm contacts the same way it fans out over job contacts — the
// only difference is the tag prefix (`fc:` vs `cr:`) and the enum.
// Mirrors `firmContactCategoryValidator` in
// `convex/contacts/firmContacts/schema.ts`.
// ---------------------------------------------------------------------------

/** Firm-contact category — mirrors `FirmContactCategory` in the schema. */
export type FirmContactCategory =
  "bank" | "regulator" | "professional" | "org_member" | "referrer" | "other";

export interface FirmCategoryMeta {
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  badgeColor: BadgeColor;
}

export const FIRM_CATEGORY_META: Record<FirmContactCategory, FirmCategoryMeta> =
  {
    bank: {
      label: "Banks",
      shortLabel: "Bank",
      icon: Landmark,
      badgeColor: "blue",
    },
    regulator: {
      label: "Regulators",
      shortLabel: "Regulator",
      icon: Scale,
      badgeColor: "amber",
    },
    professional: {
      label: "Professionals",
      shortLabel: "Professional",
      icon: Briefcase,
      badgeColor: "purple",
    },
    org_member: {
      label: "Firm Members",
      shortLabel: "Firm Member",
      icon: UserCog,
      badgeColor: "emerald",
    },
    referrer: {
      label: "Referrers",
      shortLabel: "Referrer",
      icon: Handshake,
      badgeColor: "teal",
    },
    other: {
      label: "Other",
      shortLabel: "Other",
      icon: Contact,
      badgeColor: "gray",
    },
  };

/** Stable display order for firm categories. */
export const FIRM_CATEGORY_ORDER: ReadonlyArray<FirmContactCategory> = [
  "bank",
  "regulator",
  "professional",
  "org_member",
  "referrer",
  "other",
];

/**
 * Prefix used for firm-contact category tags stored in
 * `precedentTemplates.tags`. Parallel to the `cr:` contact-role prefix.
 */
export const FIRM_CONTACT_TAG_PREFIX = "fc:" as const;

/** Build the canonical tag string for a firm-contact category. */
export function firmContactTag(category: FirmContactCategory): string {
  return `${FIRM_CONTACT_TAG_PREFIX}${category}`;
}

/** Type guard for whether a raw string is a known `FirmContactCategory`. */
export function isFirmContactCategory(
  value: string,
): value is FirmContactCategory {
  return FIRM_CATEGORY_ORDER.some((category) => category === value);
}

/**
 * Parse an `fc:*` tag back into its `FirmContactCategory`. Returns `null`
 * for tags that don't carry the prefix or whose value isn't a known
 * category.
 */
export function parseFirmContactTag(tag: string): FirmContactCategory | null {
  if (!tag.startsWith(FIRM_CONTACT_TAG_PREFIX)) return null;
  const value = tag.slice(FIRM_CONTACT_TAG_PREFIX.length);
  return isFirmContactCategory(value) ? value : null;
}

/**
 * Prefix for user-defined firm-contact category tags (MOR-914). The value
 * is the `firmContactCategories` row id, so tags survive category renames.
 */
export const FIRM_CUSTOM_CATEGORY_TAG_PREFIX = "fcc:" as const;

/** Build the tag string for a custom firm-contact category id. */
export function firmContactCustomCategoryTag(categoryId: string): string {
  return `${FIRM_CUSTOM_CATEGORY_TAG_PREFIX}${categoryId}`;
}

/** Parse an `fcc:*` tag back into the custom category id, or `null`. */
export function parseFirmContactCustomCategoryTag(tag: string): string | null {
  if (!tag.startsWith(FIRM_CUSTOM_CATEGORY_TAG_PREFIX)) return null;
  const value = tag.slice(FIRM_CUSTOM_CATEGORY_TAG_PREFIX.length);
  return value || null;
}

/**
 * Prefix for firm-contact address-state tags (MOR-913). The value is the
 * upper-cased state code (NSW, VIC, …); a tagged template fans out over
 * every firm contact whose first address state matches.
 */
export const FIRM_CONTACT_STATE_TAG_PREFIX = "fcs:" as const;

/** Build the tag string for a firm-contact address state. */
export function firmContactStateTag(state: string): string {
  return `${FIRM_CONTACT_STATE_TAG_PREFIX}${state.toUpperCase()}`;
}

/**
 * Dynamic firm-contact state tag: instead of naming a fixed state, narrow
 * the fan-out to firm contacts whose address state matches the JOB's state
 * (registered office, falling back to principal place of business — see
 * {@link jobAddressState}). Lets one "Letter to Sheriff" template serve
 * every state rather than one template per state. When the job has no
 * state recorded yet the tag imposes no restriction, consistent with the
 * fixed `fcs:*` rule that states narrow but never require.
 */
export const FIRM_CONTACT_JOB_STATE_TAG = "fcs:job" as const;

/** True when `tag` is the dynamic "match the job's state" tag. */
export function isFirmContactJobStateTag(tag: string): boolean {
  return tag.toLowerCase() === FIRM_CONTACT_JOB_STATE_TAG;
}

/**
 * Parse an `fcs:*` tag back into the upper-cased state code, or `null`.
 * The dynamic {@link FIRM_CONTACT_JOB_STATE_TAG} is deliberately NOT a
 * state code and parses to `null`; check it with
 * {@link isFirmContactJobStateTag} instead.
 */
export function parseFirmContactStateTag(tag: string): string | null {
  if (!tag.startsWith(FIRM_CONTACT_STATE_TAG_PREFIX)) return null;
  if (isFirmContactJobStateTag(tag)) return null;
  const value = tag.slice(FIRM_CONTACT_STATE_TAG_PREFIX.length).toUpperCase();
  return value || null;
}

/**
 * The state a job "belongs to" for firm-contact fan-out: the registered
 * office state, falling back to the principal place of business. Returns
 * the trimmed, upper-cased code (matching how `fcs:*` tags and firm
 * contact addresses are compared) or `null` when neither is recorded.
 */
export function jobAddressState(
  job:
    | { registeredOfficeState?: string; principalPlaceState?: string }
    | null
    | undefined,
): string | null {
  if (!job) return null;
  const candidate =
    job.registeredOfficeState?.trim() || job.principalPlaceState?.trim();
  return candidate ? candidate.toUpperCase() : null;
}

/**
 * Job-contact narrowing tag: restrict a template's job-contact fan-out to
 * company contacts that are in external administration (non-empty
 * `externalAdminStatus`). Like `fcs:*` for firm contacts it NARROWS the
 * `cr:*` / `jcr:*` roles ticked alongside it; on its own it selects every
 * company in external administration on the job. Used by letters addressed
 * to a related company's appointee, which bind the
 * `contact_job_external_admin_*` fields.
 */
export const JOB_CONTACT_EXTERNAL_ADMIN_TAG = "jcf:external_admin" as const;

/** True when `tag` is the external-administration narrowing tag. */
export function isJobContactExternalAdminTag(tag: string): boolean {
  return tag === JOB_CONTACT_EXTERNAL_ADMIN_TAG;
}
